// Preview d'une entrée. Deux modes :
//   WebRTC : latence sous la seconde, mais sans le son AAC et avec le port UDP 8189 joignable ;
//   HLS    : quelques secondes de latence, avec le son, passe partout (HTTP via l'API).
// Démarre en WebRTC et passe seul en HLS si WebRTC échoue (pare-feu, réseau d'entreprise...), ou s'il est connecté
// sans afficher d'image (les navigateurs ne décodent pas en WebRTC la vidéo avec images B, fréquente chez les
// encodeurs broadcast). Images B déjà détectées par l'analyse du contenu : démarre directement en HLS. Si le HLS n'affiche rien non plus, aucune cause n'est affirmée : la conversion en
// progressif n'est proposée que si l'analyse du contenu a réellement détecté de l'entrelacé sur l'entrée.
// hls.js (environ 500 ko) n'est chargé qu'au premier passage en HLS.
import type Hls from "hls.js";
import { useEffect, useRef, useState } from "react";
import { N_, t } from "../lib/i18n";
import { startWhep, type WhepSession } from "../lib/whep";
import { Button, Segmented } from "./ui";

type Mode = "webrtc" | "hls";
type PlayerState = "idle" | "connecting" | "playing" | "error";

const WEBRTC_TIMEOUT = 6000;   // sans connexion après ce délai : repli HLS
const FRAME_TIMEOUT = 4000;    // connecté mais sans image décodée après ce délai : repli HLS
const HLS_FRAME_TIMEOUT = 15000;
const B_FRAMES_REASON = N_("images B dans la vidéo, que le WebRTC des navigateurs n'affiche pas (sur l'encodeur, désactiver les images B pour une preview en temps réel)");

/** Images vidéo réellement décodées par le navigateur. */
function decodedFrames(el: HTMLVideoElement): number {
  return el.getVideoPlaybackQuality?.().totalVideoFrames ?? (el.videoWidth > 0 ? 1 : 0);
}

/** Appelle onStall si aucune image n'est décodée dans le délai ; renvoie la fonction d'arrêt. */
function watchFrames(el: HTMLVideoElement, timeout: number, onStall: () => void): () => void {
  const start = Date.now();
  const timer = window.setInterval(() => {
    if (decodedFrames(el) > 0) window.clearInterval(timer);
    else if (Date.now() - start > timeout) { window.clearInterval(timer); onStall(); }
  }, 500);
  return () => window.clearInterval(timer);
}

export function WhepPlayer({ inputId, online, video: hasVideo = true, interlaced = false, bFrames = false }: {
  inputId: string; online: boolean; video?: boolean;
  /** Entrelacé détecté par l'analyse du contenu (et pas encore converti) */
  interlaced?: boolean;
  /** Images B détectées par l'analyse du contenu */
  bFrames?: boolean;
}) {
  // lu au moment du constat, sans relancer la preview quand la détection arrive
  const interlacedRef = useRef(interlaced);
  interlacedRef.current = interlaced;
  const bFramesRef = useRef(bFrames);
  bFramesRef.current = bFrames;
  const video = useRef<HTMLVideoElement>(null);
  // images B déjà connues : inutile d'attendre l'échec du WebRTC
  const [mode, setMode] = useState<Mode>(bFrames ? "hls" : "webrtc");
  const [state, setState] = useState<PlayerState>("idle");
  const [error, setError] = useState<string | null>(null);
  // raison du passage en HLS (null : pas de repli) ; distincte de error, effacée à chaque démarrage de la preview
  const [fallback, setFallback] = useState<string | null>(bFrames ? B_FRAMES_REASON : null);
  const [muted, setMuted] = useState(true);
  const [wanted, setWanted] = useState(true);
  const [hasAudio, setHasAudio] = useState(false);

  useEffect(() => {
    const el = video.current;
    if (!wanted || !online || !el) return;
    let cancelled = false;
    let session: WhepSession | null = null;
    let hls: Hls | null = null;
    setState("connecting");
    setError(null);

    const fallBack = (reason: string) => {
      if (cancelled) return;
      setFallback(reason);
      setMode("hls");
    };

    if (mode === "webrtc") {
      const timer = window.setTimeout(() => fallBack(t("WebRTC ne répond pas (port UDP 8189 bloqué ?)")), WEBRTC_TIMEOUT);
      let stopWatch = () => {};
      startWhep(inputId, el, (s) => {
        if (s === "connected") {
          window.clearTimeout(timer);
          setState("playing");
          setHasAudio(el.srcObject instanceof MediaStream && el.srcObject.getAudioTracks().length > 0);
          if (hasVideo) {   // flux audio seul : pas d'image à attendre
            stopWatch = watchFrames(el, FRAME_TIMEOUT, () => fallBack(bFramesRef.current
              ? t(B_FRAMES_REASON) : t("le navigateur n'affiche pas ce flux en temps réel")));
          }
        }
        if (s === "failed") fallBack(t("Connexion WebRTC perdue"));
      })
        .then((s) => { if (cancelled) s.close(); else session = s; })
        .catch((e: Error) => { window.clearTimeout(timer); fallBack(e.message); });
      return () => {
        cancelled = true;
        window.clearTimeout(timer);
        stopWatch();
        session?.close();
        setState("idle");
      };
    }

    // HLS : hls.js (Chrome, Edge, Firefox) ou lecture native (Safari)
    const src = `/api/inputs/${encodeURIComponent(inputId)}/hls/index.m3u8`;
    const onPlaying = () => { setState("playing"); setHasAudio(true); };
    el.addEventListener("playing", onPlaying);
    const stopHlsWatch = !hasVideo ? () => {} : watchFrames(el, HLS_FRAME_TIMEOUT, () => {
      // seulement si l'entrelacé est vraiment détecté : sinon aucune cause n'est affirmée
      if (cancelled || !interlacedRef.current) return;
      setState("error");
      setError(t("Source entrelacée détectée sur cette entrée : le navigateur ne l'affiche pas. Dans l'entrée, « Source entrelacée » : convertir en progressif."));
    });
    const play = () => el.play().catch(() => { /* lecture auto bloquée : l'opérateur clique sur Lancer */ });
    if (el.canPlayType("application/vnd.apple.mpegurl")) {
      el.src = src;   // Safari : HLS natif
      play();
    } else {
      import("hls.js").then(({ default: HlsJs }) => {
        if (cancelled) return;
        if (!HlsJs.isSupported()) {
          setState("error");
          setError(t("Ce navigateur ne lit ni WebRTC ni HLS."));
          return;
        }
        hls = new HlsJs({ lowLatencyMode: false, liveSyncDurationCount: 2, backBufferLength: 10 });
        hls.on(HlsJs.Events.ERROR, (_e, data) => {
          if (data.fatal && !cancelled) {
            setState("error");
            setError(t("Preview HLS indisponible ({details})", { details: data.details }));
          }
        });
        hls.loadSource(src);
        hls.attachMedia(el);
        play();
      }).catch(() => { setState("error"); setError(t("Lecteur HLS impossible à charger.")); });
    }
    return () => {
      cancelled = true;
      stopHlsWatch();
      el.removeEventListener("playing", onPlaying);
      hls?.destroy();
      el.removeAttribute("src");
      el.load();
      setState("idle");
    };
  }, [inputId, online, wanted, mode, hasVideo]);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative aspect-video w-full overflow-hidden rounded-md border border-line bg-black">
        <video ref={video} autoPlay playsInline muted={muted} className="size-full object-contain" />
        {state !== "playing" && (
          <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-muted">
            {!online ? t("Pas de signal sur cette entrée.")
              : !wanted ? t("Preview arrêtée.")
              : state === "connecting" ? (mode === "hls" ? t("Chargement de la preview HLS (quelques secondes)…") : t("Connexion à la preview…"))
              : state === "error" ? <span className="text-err">{error}</span>
              : null}
          </div>
        )}
        {state === "playing" && (
          <span className="absolute top-2 left-2 rounded bg-black/70 px-1.5 py-px font-mono text-[11px] text-ok">
            {mode === "webrtc" ? "WebRTC" : "HLS"}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => setWanted((w) => !w)} disabled={!online}>
          {wanted ? t("Arrêter la preview") : t("Lancer la preview")}
        </Button>
        <Segmented label={t("Mode de preview")} value={mode} onChange={(m) => { setFallback(null); setMode(m); }}
          options={[{ value: "webrtc", label: t("Temps réel") }, { value: "hls", label: t("Avec le son (HLS)") }]} />
        {state === "playing" && hasAudio && (
          <Button size="sm" variant="ghost" onClick={() => setMuted((m) => !m)}>{muted ? t("Activer le son") : t("Couper le son")}</Button>
        )}
      </div>
      <p className="text-[12px] text-faint">
        {fallback && mode === "hls" ? t("Preview en HLS : {reason}.", { reason: fallback })
          : mode === "webrtc" ? (state === "playing" && !hasAudio
            ? t("Temps réel sans le son : WebRTC ne lit que l'audio Opus (pas l'AAC). Choisissez « Avec le son » pour l'écouter.")
            : t("Temps réel : latence inférieure à la seconde."))
          : t("HLS : 2 à 5 s de retard, avec le son.")}
      </p>
    </div>
  );
}
